import { Link } from '@tanstack/react-router'

import { buttonClass } from '@/components/button-class'
import { Card, Page } from '@/components/layout'
import { LandingFeedbackButtons } from '@/features/feedback/landing-buttons'
import { useCustomerAuth } from '@/features/ordering/customer-auth'
import { CustomerHeader, CustomerShell } from '@/features/ordering/customer-chrome'
import type { MessageKey } from '@/lib/i18n/messages/en'
import { useT } from '@/lib/i18n/translate'

/** Who to ask beyond the one self-serve restaurant (K1; the platform links more). */
const CONTACT_EMAIL = 'nguyennta@icloud.com'
const CONTACT_PHONE = { shown: '(+84) 77 777 5995', tel: '+84777775995' }

const BENEFITS = [1, 2, 3, 4] as const
const FEATURES = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const
const STEPS = [1, 2, 3, 4] as const
const PRICING_EXAMPLES = [1, 2] as const

/**
 * `/` — the Romi intro (PRD "Root Romi landing page"), text carried over from
 * the old project. Same chrome as every public page; the account pages are
 * under the header's avatar menu.
 */
export function RomiLandingPage() {
  const t = useT()
  const auth = useCustomerAuth()
  return (
    <CustomerShell color={undefined} footer="copyright">
      <CustomerHeader detail={t('landing.tagline')} auth={auth} />
      <Page width="lg" className="flex flex-1 flex-col gap-4">
        <LandingFeedbackButtons auth={auth} />
        <Card className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-semibold">{t('landing.whatTitle')}</h2>
          <p className="text-muted">{t('landing.what')}</p>
        </Card>
        <Card className="flex flex-col gap-4 p-6">
          <h2 className="text-xl font-semibold">{t('landing.whyTitle')}</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {BENEFITS.map((n) => (
              <li key={n} className="border-border rounded-lg border p-4">
                <p className="font-medium">{t(`landing.why${n}.title` as MessageKey)}</p>
                <p className="text-muted text-sm">{t(`landing.why${n}.body` as MessageKey)}</p>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="flex flex-col gap-4 p-6">
          <h2 className="text-xl font-semibold">{t('landing.featuresTitle')}</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {FEATURES.map((n) => (
              <li key={n} className="flex gap-2">
                <span aria-hidden className="text-accent-ink">
                  ✓
                </span>
                {t(`landing.feature${n}` as MessageKey)}
              </li>
            ))}
          </ul>
        </Card>
        <Card className="flex flex-col gap-4 p-6">
          <h2 className="text-xl font-semibold">{t('landing.howTitle')}</h2>
          <ol className="flex flex-col gap-3">
            {STEPS.map((n) => (
              <li key={n} className="flex gap-3">
                <span
                  aria-hidden
                  className="bg-accent text-accent-fg flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                >
                  {n}
                </span>
                <div>
                  <p className="font-medium">{t(`landing.how${n}.title` as MessageKey)}</p>
                  <p className="text-muted text-sm">{t(`landing.how${n}.body` as MessageKey)}</p>
                </div>
              </li>
            ))}
          </ol>
        </Card>
        <Card className="flex flex-col gap-4 p-6">
          <h2 className="text-xl font-semibold">{t('landing.pricingTitle')}</h2>
          <div>
            <p className="text-muted">{t('landing.pricingExpected')}</p>
            <p className="text-accent-ink text-2xl font-semibold">{t('landing.pricingPrice')}</p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {PRICING_EXAMPLES.map((n) => (
              <li key={n} className="border-border rounded-lg border p-4">
                <p>{t(`landing.pricingEx${n}.title` as MessageKey)}</p>
                <p className="font-semibold">{t(`landing.pricingEx${n}.body` as MessageKey)}</p>
              </li>
            ))}
          </ul>
          <p className="font-medium">{t('landing.pricingReward')}</p>
          <p className="text-muted text-sm">{t('landing.pricingNote')}</p>
          <div>
            <Link to="/app/restaurants/new" className={buttonClass()}>
              {t('landing.startFree')}
            </Link>
          </div>
        </Card>
        <Card className="flex flex-col items-center gap-4 p-6 text-center">
          <h2 className="text-xl font-semibold">{t('landing.contactTitle')}</h2>
          <p className="text-muted">{t('landing.contact')}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/app/restaurants/new" className={buttonClass()}>
              {t('landing.createRestaurant')}
            </Link>
            <Link
              to="/app/guide/$guide"
              params={{ guide: 'create-restaurant' }}
              className="border-border hover:bg-accent-soft min-h-tap flex items-center rounded-lg border px-4 font-medium"
            >
              {t('landing.guide')}
            </Link>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="border-border hover:bg-accent-soft min-h-tap flex items-center rounded-lg border px-4 font-medium"
            >
              {CONTACT_EMAIL}
            </a>
            <a
              href={`tel:${CONTACT_PHONE.tel}`}
              className="border-border hover:bg-accent-soft min-h-tap flex items-center rounded-lg border px-4 font-medium"
            >
              {CONTACT_PHONE.shown}
            </a>
          </div>
        </Card>
      </Page>
    </CustomerShell>
  )
}
